import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as fbSignOut,
  type User,
} from 'firebase/auth';
import { doc, getDoc, collection, query, where, getDocs, limit } from 'firebase/firestore';
import { auth, db } from '../firebase';

export interface MemberContext {
  householdId: string;
  role: 'parent' | 'applicant';
  applicantId: string | null;
}

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  membership: MemberContext | null;
  signUp: (email: string, password: string) => Promise<User>;
  signIn: (email: string, password: string) => Promise<User>;
  signInWithGoogle: () => Promise<User>;
  signOut: () => Promise<void>;
  refreshMembership: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [membership, setMembership] = useState<MemberContext | null>(null);

  async function findMembership(uid: string): Promise<MemberContext | null> {
    if (!db) return null;
    // Look up the member record. We use a collection group query to find any
    // member doc with this user id across all households.
    try {
      const memberDocs = await getDocs(
        query(
          collection(db, 'households'),
          // We can't do a collectionGroup query directly without an index,
          // so for v0.2 we list households the user can read (rules already
          // limit to their own), then check member docs. Simpler approach:
          // store a top-level user_household_index when household is created.
          // But for now, scan via known household path stored in user meta.
          where('member_uids', 'array-contains', uid),
          limit(1)
        )
      );
      if (memberDocs.empty) return null;
      const householdDoc = memberDocs.docs[0];
      const householdId = householdDoc.id;
      const memberRef = doc(db, 'households', householdId, 'members', uid);
      const memberSnap = await getDoc(memberRef);
      if (!memberSnap.exists()) return null;
      const data = memberSnap.data();
      return {
        householdId,
        role: data.role,
        applicantId: data.applicant_id ?? null,
      };
    } catch (e) {
      console.error('findMembership failed', e);
      return null;
    }
  }

  async function refreshMembership() {
    if (!user) {
      setMembership(null);
      return;
    }
    const m = await findMembership(user.uid);
    setMembership(m);
  }

  useEffect(() => {
    if (!auth) {
      setLoading(false);
      return;
    }
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        const m = await findMembership(u.uid);
        setMembership(m);
      } else {
        setMembership(null);
      }
      setLoading(false);
    });
    return () => unsub();
  }, []);

  async function signUp(email: string, password: string) {
    if (!auth) throw new Error('Firebase not configured');
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    return cred.user;
  }

  async function signIn(email: string, password: string) {
    if (!auth) throw new Error('Firebase not configured');
    const cred = await signInWithEmailAndPassword(auth, email, password);
    return cred.user;
  }

  async function signInWithGoogle() {
    if (!auth) throw new Error('Firebase not configured');
    const provider = new GoogleAuthProvider();
    const cred = await signInWithPopup(auth, provider);
    return cred.user;
  }

  async function signOut() {
    if (!auth) return;
    await fbSignOut(auth);
  }

  return (
    <AuthContext.Provider
      value={{ user, loading, membership, signUp, signIn, signInWithGoogle, signOut, refreshMembership }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
